import { auteurPourRequete, cadrerParAuteur } from './portees.js';
import {
  type CollectionReference,
  type DocumentData,
  type Query,
  type QueryConstraint,
  type QueryDocumentSnapshot,
  average as averageOf,
  collection,
  documentId,
  getAggregateFromServer,
  getCountFromServer,
  getDocs,
  limit as limitTo,
  orderBy as orderByField,
  query,
  startAfter,
  sum as sumOf,
  where,
} from 'firebase/firestore';

/**
 * LA SURFACE DE LISTE DU SOCLE, ecrite UNE fois pour les 56 repositories.
 *
 * Avant ce module, chaque repository portait sa propre copie de
 * `getDocs(query(col, where('orgId','==',orgId)))` : aucun argument, donc
 * chaque ecran chargeait la collection ENTIERE de l'organisation puis
 * filtrait, triait et fenetrait cote client. Sur 15 chats ca ne se voit pas,
 * sur 10 000 contacts c'est 10 000 lectures Firestore, 10 000 validations Zod
 * et un onglet fige. Et `archivedAt` etait ecrit partout mais filtre nulle
 * part : les elements archives remontaient dans les listes.
 *
 * Une seule copie de la semantique, pour que les 56 familles ne puissent pas
 * diverger : le cadrage `orgId` (exige par les Security Rules), l'exclusion des
 * archives, le filtre de sous-type, le tri, la taille de page et le curseur.
 *
 * CE QUE CE MODULE NE FAIT PAS, volontairement :
 *   - aucun tri par defaut. Une requete `list()` sans argument garde EXACTEMENT
 *     l'ordre d'avant (celui de Firestore, par identifiant de document). Poser
 *     un tri par defaut aurait change en silence l'ordre de tous les ecrans
 *     existants, et surtout aurait exige un index composite par famille (voir
 *     la note sur les index plus bas) ;
 *   - aucune recherche plein texte : Firestore n'en fait pas. La recherche
 *     reste cliente, sur les pages deja chargees, et le gabarit le dit a
 *     l'ecran plutot que de laisser croire le contraire.
 *
 * NOTE SUR LES INDEX FIRESTORE, a lire avant d'ajouter un `orderBy` :
 * une requete qui n'a que des filtres d'EGALITE (`orgId`, `archivedAt`, `type`,
 * et les cles de `where`) se sert des index a champ unique que Firestore cree
 * tout seul, meme avec `limit` et `startAfter`. Rien a declarer. Des qu'un
 * `orderBy` porte sur un AUTRE champ que ceux-la, il faut un index composite
 * declare dans `firebase/firestore.indexes.json` et deploye, sinon la requete
 * echoue en production avec `failed-precondition`. `describeMissingIndex` ci-dessous
 * rend le bloc JSON exact a coller. Le fichier d'index ne peut pas etre
 * exhaustif (55 familles multipliees par leurs champs depasseraient le plafond
 * de 200 index composites de Firestore) : il porte les tris reellement utilises,
 * et c'est l'app qui declare le sien quand elle en ouvre un nouveau.
 *
 * LE PIEGE DU CHAMP ABSENT, deux fois le meme mecanisme, deux consequences
 * opposees, et aucune des deux ne fait d'erreur :
 *   - `where('archivedAt', '==', null)` ne rattrape PAS un document ou le champ
 *     est absent : ce document disparait de toutes les listes ;
 *   - `orderBy('status')` EXCLUT tout document ou `status` est absent : trier
 *     par un champ facultatif retire silencieusement des lignes du resultat.
 * Consequence directe a connaitre : `count()` ignore `orderBy` par conception,
 * donc sur un champ facultatif il compte PLUS de documents que la liste triee
 * n'en rendra jamais. Un total et une liste qui ne tombent pas juste ne sont
 * pas forcement un bug de pagination : commencez par regarder si le champ trie
 * est facultatif.
 *
 * LES TROIS ETATS. Un document est ACTIF
 * (`archivedAt` null, `deletedAt` null), ARCHIVE (`archivedAt` pose, `deletedAt`
 * null) ou SUPPRIME (les DEUX poses). Supprimer pose aussi `archivedAt` pour une
 * raison tres concrete : le filtre serveur `archivedAt == null` existe deja et
 * les index composites le portent deja, donc un document supprime sort de toutes
 * les listes sans qu'on touche a une seule requete ni a un seul index.
 * En contrepartie, les supprimes remontent dans `archived: 'only'` et
 * `archived: 'all'` (ils PORTENT `archivedAt`), et `runList` les retire COTE
 * CLIENT, apres lecture. Quand une page limitee en contient, `runList` poursuit
 * la lecture depuis le dernier document vu par le serveur jusqu'a remplir la
 * page ou atteindre la fin. Le contrat historique reste donc vrai : une page
 * courte signifie qu'il n'y a plus de ligne visible a lire.
 *
 * ET LES AGREGATS, EUX, SORTENT LES SUPPRIMES AU SERVEUR (09/09/2026). Un
 * agregat ne lit aucun document : il ne peut donc pas les retirer apres coup
 * comme `runList` le fait, et sur `'only'` et `'all'` il depassait la liste
 * d'exactement la taille de la corbeille. `aggregateFilters` ajoute donc
 * `where('deletedAt', '==', null)` des que le mode d'archives ne l'a pas deja
 * exclu, et les deux chiffres se rejoignent. En `'exclude'` (le defaut, donc
 * toutes les listes d'aujourd'hui), rien ne change et rien n'a a changer : un
 * supprime porte `archivedAt`, il etait deja hors du compte.
 *
 * CE QUE CE FILTRE SUPPOSE, et c'est la seule chose a verifier avant d'y
 * toucher : que TOUT document porte la clef `deletedAt`. Le SDK la pose a la
 * creation (`deletedAt: null` dans les 55 familles), et les documents anterieurs
 * au chantier des trois etats se rattrapent par `scripts/backfill-deleted-at.mjs`.
 * Sans elle, le piege du champ absent ci-dessus s'appliquerait au compte plutot
 * qu'a la liste : un total qui BAISSE, jamais une erreur.
 *
 * LA LISTE, ELLE, NE PORTE PAS CETTE EGALITE, et ce n'est pas une inconsequence.
 * Une liste FENETRE et PAGINE : un document sans la clef y disparaitrait
 * purement et simplement, et il ne resterait rien a l'ecran pour le dire. Un
 * agregat ne rend qu'un nombre, et un nombre qui baisse se voit. Le jour ou le
 * backfill aura tourne partout, la liste pourra suivre ; d'ici la, elle garde
 * son filtre client, avec la limite de page ecrite juste au-dessus.
 */

/** Sens de tri. */
export type ListDirection = 'asc' | 'desc';

/**
 * Ce qu'une liste fait des elements ARCHIVES. Defaut : `'exclude'`.
 *
 *   - `'exclude'` : `where('archivedAt', '==', null)`, les actifs seuls. C'est
 *     le comportement historique, strictement inchange ;
 *   - `'only'`    : `where('archivedAt', '!=', null)`, le mode Archives ;
 *   - `'all'`     : aucun filtre sur `archivedAt`.
 *
 * Les documents SUPPRIMES ne remontent dans aucun des trois : ils portent
 * `archivedAt`, donc `'only'` et `'all'` les ramenent du serveur, et `runList`
 * les retire ensuite cote client (voir l'en-tete du module pour la limite que
 * cela impose a la taille d'une page).
 */
export type ArchivedMode = 'exclude' | 'only' | 'all';

/** Un tri, forme longue. La forme courte est le nom du champ seul. */
export interface ListOrder {
  field: string;
  /** Defaut : `asc`. */
  dir?: ListDirection;
}

/**
 * Les options communes a `list()` sur les 56 familles.
 *
 * Toutes facultatives : `list()` sans argument reste valide et ne casse aucun
 * appelant. Seule difference de comportement avec l'ancienne version, et elle
 * est voulue : les elements ARCHIVES ne remontent plus.
 */
export interface ListOptions {
  /**
   * Sous-type metier de la famille (`type: 'bike'`, `type: 'cat'`). Ignore par
   * les familles qui n'ont pas de discriminant (`contacts`).
   */
  type?: string;
  /**
   * DES EGALITES DE PLUS, en ET avec le cadrage que le SDK pose deja (`orgId`,
   * `type`, `archivedAt`) : `{ where: { assetId: 'a1' } }` devient
   * `where('assetId', '==', 'a1')`. Une cle par filtre, dans l'ordre ou l'objet
   * les porte, et rien d'autre : ni inegalite, ni `in`.
   *
   * UN CHEMIN POINTE EST UN CHAMP COMME UN AUTRE :
   * `{ where: { 'customFields.materielId': 'm1' } }` vise le sac des champs
   * SUR-MESURE, la ou une app range ce que la famille ne porte pas
   * (AGENTS.md section 7). C'est exactement ce dont un LIEN ENTRE PORTES a
   * besoin quand il passe par un champ sur-mesure : viser la clef nue ne
   * retiendrait rien, sans la moindre erreur.
   *
   * C'EST CE QUI REND UN LIEN ENTRE PORTES LISIBLE COTE SERVEUR. Sans lui, une
   * fiche qui montre "les interventions de CE materiel" chargeait la famille
   * entiere et filtrait en memoire : le compteur au-dessus de la liste comptait
   * alors la page, pas le lien (cf. `runCount`).
   *
   * QUATRE CLES SONT REFUSEES, avec une erreur plutot qu'en silence :
   * `orgId` (le cadrage, pose par le SDK et exige par les rules), `type`
   * (l'option `type`), `archivedAt` (l'option `archived`) et `deletedAt` (les
   * supprimes, cf. `runList`). Les accepter ici laisserait deux filtres se
   * contredire sur le meme champ, et Firestore rendrait alors zero ligne sans
   * la moindre erreur : l'ecran afficherait "aucun element" et personne ne
   * chercherait du cote du code appelant. Une valeur `undefined` est refusee
   * pour la meme raison (`{ where: { assetId: undefined } }` est presque
   * toujours une variable oubliee, jamais "filtre sur rien"). Un objet VIDE,
   * lui, n'est pas une faute : il ne pose aucun filtre.
   *
   * Le piege du champ ABSENT vaut ici aussi, et il est en tete de module :
   * `where: { assetId: null }` ne rattrape pas les documents ou `assetId`
   * n'existe pas.
   */
  where?: Record<string, unknown>;
  /**
   * Taille de la page. Absent : aucune limite (comportement d'avant), ce qui
   * reste legitime sur une collection de reglages ou de catalogues.
   */
  limit?: number;
  /**
   * Curseur : l'identifiant du DERNIER document de la page precedente. Le
   * document est relu pour construire le curseur Firestore, ce qui coute une
   * lecture par page et garantit que le curseur reste valide meme avec un tri
   * sur un champ metier.
   */
  after?: string;
  /** Tri serveur. Exige un index composite (voir l'en-tete du module). */
  orderBy?: string | ListOrder;
  /**
   * Inclure les elements archives (`archivedAt` non nul). Defaut : false.
   *
   * @deprecated Utilisez `archived` : `includeArchived: true` vaut
   * `archived: 'all'`, et `includeArchived: false` (ou absent) vaut
   * `archived: 'exclude'`. L'option reste ACCEPTEE et se comporte exactement
   * comme avant : aucun appelant existant n'a a bouger. Elle ne sait juste pas
   * dire `'only'` (le mode Archives), qui est la raison d'etre de `archived`.
   */
  includeArchived?: boolean;
  /**
   * Ce que la liste fait des elements archives. Defaut : `'exclude'`.
   *
   * Si `archived` ET `includeArchived` sont fournis, `archived` GAGNE : c'est
   * l'option precise, `includeArchived` n'est plus qu'un alias historique. On
   * ne leve pas sur la combinaison des deux, parce qu'elle est le cas normal
   * d'un appelant qui migre (il ajoute `archived` a un objet d'options qui
   * portait deja l'ancien drapeau) : lever la ferait echouer une migration qui
   * marche.
   */
  archived?: ArchivedMode;
  /**
   * L'ACCES PAR FICHE (`docs/socle/acces-par-fiche.md`), sur les seules
   * familles qui le portent (`FAMILLES_ACCES_PAR_FICHE`, @taki/data-model) :
   *   - `'equipe'` (defaut) : les fiches de l'equipe, ce que toute liste
   *     rendait avant le chantier ;
   *   - `'prive'` : MES fiches privees et celles qu'on m'a partagees
   *     (`acces.participants array-contains <mon personId>`) ; sans fiche
   *     membre, donc sans `personId`, la liste est vide et rien ne part ;
   *   - `{ ancre }` : les fiches ancrees sur CETTE fiche (le chatter d'un
   *     contact) ;
   *   - `{ conversation }` : les messages de CETTE conversation (familles
   *     heritieres seulement, `messages`).
   *
   * POURQUOI LE SDK LE POSE TOUJOURS : Firestore juge une requete `list` contre
   * ce qu'elle PEUT rendre ; une requete qui pourrait rendre une fiche privee
   * est refusee en bloc. La requete doit donc fixer ce que les rules lisent,
   * et c'est le SDK qui le fait, jamais l'ecran. Les clefs `acces.*` et
   * `conversationId` sont refusees dans `where` pour la meme raison que
   * `orgId`. Sur une famille sans acces par fiche, l'option est refusee.
   */
  acces?: AccesDeListe;
}

/** L'option `acces` d'une liste (cf. `ListOptions.acces`). */
export type AccesDeListe =
  | 'equipe'
  | 'prive'
  | { ancre: { family: string; recordId: string } }
  | { conversation: string };

/**
 * CE QUE LE REPOSITORY SAIT DE SA FAMILLE, pour traduire `acces` : son ROLE
 * (`porteuse`, la fiche porte ses participants ; `heritiere`, la fiche porte
 * `conversationId`) et le `personId` de l'appelant, resolu par le repository
 * AVANT d'appeler ce module (une lecture de sa fiche membre, gardee), et
 * seulement quand la liste est privee. Absent : la famille n'a pas d'acces par
 * fiche, et l'option `acces` est refusee.
 */
export interface CadrageAcces {
  role: 'porteuse' | 'heritiere';
  personId: string | null;
}

/**
 * Les options des TROIS AGREGATS (`count`, `sum`, `average`),
 * VOLONTAIREMENT plus etroites que celles de `list()`.
 *
 * Agreger une page n'a pas de sens, et un tri ne change ni un total, ni une
 * somme, ni une moyenne. Plutot que d'accepter `limit`, `after` et `orderBy`
 * pour les ignorer en silence (un appelant qui reutilise le meme objet
 * d'options pour les deux appels obtiendrait alors un chiffre qui ne
 * correspond a rien de ce qu'il a demande), le TYPE les refuse : l'erreur se
 * voit a la compilation. C'est la regle, et elle n'a qu'une phrase : un agregat
 * prend le CADRAGE de la liste (`type`, `where`, `archived`), jamais sa
 * fenetre.
 *
 * UN SEUL NOM POUR LES TROIS, et pas un alias par verbe : les options sont les
 * MEMES, et deux noms pour un seul fait finissent toujours par diverger.
 *
 * A SAVOIR sur `archived` ici, et c'est le SEUL ecart avec le cadrage d'une
 * liste : en `'only'` et en `'all'`, un agregat ajoute `deletedAt == null` au
 * SERVEUR (`aggregateFilters`), la ou la liste retire les supprimes cote client
 * apres lecture. Meme jeu de documents des deux cotes, par deux chemins que
 * rien d'autre ne separe. `'exclude'` (le defaut) n'est pas concerne : un
 * supprime porte `archivedAt`, il est deja hors du compte.
 *
 * CE QUE CETTE EGALITE SUPPOSE : que tout document porte la clef `deletedAt`
 * (posee par le SDK a la creation, rattrapee par
 * `scripts/backfill-deleted-at.mjs` sur les documents anterieurs). Le detail
 * est en tete de ce module.
 */
export type CountOptions = Pick<ListOptions, 'type' | 'where' | 'includeArchived' | 'archived' | 'acces'>;

/**
 * Plafond dur de la taille de page. Une page plus grande n'est pas une page,
 * c'est un chargement complet deguise : autant l'ecrire sans `limit`.
 */
export const MAX_LIST_LIMIT = 1000;

/**
 * Le curseur pointe un document qui n'existe plus (archive en dur, supprime
 * cote serveur, ou identifiant invente).
 *
 * Erreur DEDIEE et non un message generique : l'appelant (le core des ecrans)
 * doit pouvoir la reconnaitre pour repartir de la premiere page au lieu
 * d'afficher une erreur a l'utilisateur. Un repli silencieux sans curseur
 * serait pire que l'erreur : la page suivante reservirait la premiere, et
 * l'utilisateur verrait des doublons sans comprendre.
 */
export class CursorLostError extends Error {
  readonly cursorId: string;
  readonly collection: string;

  constructor(collection: string, cursorId: string, why: string, options?: { cause?: unknown }) {
    super(
      `Curseur de pagination perdu : le document "${cursorId}" de la collection ` +
        `"${collection}" ne peut plus servir de curseur (${why}). ` +
        'Rechargez depuis la premiere page.',
      { cause: options?.cause instanceof Error ? options.cause : undefined },
    );
    this.name = 'CursorLostError';
    this.cursorId = cursorId;
    this.collection = collection;
  }
}

/**
 * Index composite manquant. Classe DEDIEE plutot qu'une `Error` nue, pour deux
 * raisons apprises a la relecture : le `code` de l'erreur Firestore d'origine
 * doit survivre (un appelant distingue `failed-precondition`, qu'il faut
 * corriger, de `unavailable`, qu'il faut reessayer ; l'ecraser fait reessayer
 * en boucle une requete qui ne marchera jamais), et le bloc JSON a coller doit
 * etre lisible sans passer par le texte du message.
 */
export class MissingIndexError extends Error {
  readonly code = 'failed-precondition';
  readonly index: FirestoreIndex;

  constructor(collection: string, index: FirestoreIndex, cause: unknown) {
    super(
      `Index composite manquant pour la collection "${collection}". Ajoutez ce bloc au ` +
        'tableau `indexes` de firebase/firestore.indexes.json, puis deployez ' +
        '(`firebase deploy --only firestore:indexes`) :\n' +
        JSON.stringify(index, null, 2),
      { cause: cause instanceof Error ? cause : undefined },
    );
    this.name = 'MissingIndexError';
    this.index = index;
  }
}

/** Un index composite, dans la forme exacte de `firebase/firestore.indexes.json`. */
export interface FirestoreIndex {
  collectionGroup: string;
  queryScope: 'COLLECTION';
  fields: Array<
    | { fieldPath: string; order: 'ASCENDING' | 'DESCENDING' }
    | { fieldPath: string; arrayConfig: 'CONTAINS' }
  >;
}

/**
 * LE PLAN DE REQUETE : la traduction des options en contraintes, sous forme de
 * donnees pures.
 *
 * Exister separement de la requete Firestore n'est pas une coquetterie : c'est
 * ce qui rend la semantique testable sans emulateur ni projet Firebase (les
 * tests du SDK sont unitaires, `node --test` sur le JS compile). Les regles qui
 * comptent (le cadrage `orgId` est toujours la, les archives sont exclues par
 * defaut) sont ainsi prouvees par un test, pas par une relecture.
 */
export interface ListPlan {
  /** Les filtres d'egalite, dans l'ordre ou ils sont poses. */
  filters: Array<{ field: string; value: unknown }>;
  /**
   * Les filtres d'INEGALITE, dans l'ordre ou ils sont poses. Liste SEPAREE de
   * `filters`, et pas un champ `op` ajoute a chaque entree de celle-ci, pour
   * deux raisons : `filters` est le vocabulaire des egalites partout dans ce
   * module (le Set de `describeMissingIndex`, la traduction en
   * `where(f, '==', v)`), et un champ de plus sur des entrees existantes aurait
   * change la forme d'une structure deja lue et testee ailleurs. Ici, ce qui
   * s'ajoute s'ajoute a cote.
   *
   * A CONNAITRE, c'est la regle Firestore qui decide de tout ici : un champ
   * filtre par inegalite est un champ ORDONNE. Firestore l'ajoute tout seul a
   * la fin des tris explicites (`queryNormalizedOrderBy`, firebase-js-sdk), et
   * l'index composite doit refleter cet ordre : egalites, puis tris demandes,
   * puis champs d'inegalite, puis `__name__`. C'est exactement ce que construit
   * `describeMissingIndex`.
   */
  inequalities: Array<{ field: string; op: '!='; value: unknown }>;
  /**
   * Les filtres `array-contains`, dans l'ordre ou ils sont poses. Un seul
   * aujourd'hui, `acces.participants` pour une liste privee ; liste SEPAREE
   * pour la meme raison que `inequalities`. Firestore n'accepte qu'UN
   * `array-contains` par requete : une famille qui en pose deja un (la
   * recherche de `contents.page`) ne peut pas lister le prive au serveur avec.
   */
  contains: Array<{ field: string; value: unknown }>;
  /**
   * VRAI quand la liste est privee et que l'appelant n'a pas de `personId` :
   * aucune fiche ne peut lui etre partagee, la requete serait refusee par les
   * rules. `runList` rend alors `[]` et `runCount` `0` sans rien demander.
   */
  vide: boolean;
  /**
   * Le mode d'archives RESOLU (`includeArchived` deja traduit, `archived`
   * prioritaire). Porte par le plan et pas recalcule chez l'appelant : `runList`
   * en a besoin APRES lecture pour savoir s'il doit retirer les supprimes, et
   * deux endroits qui resolvent la meme option finissent toujours par diverger.
   */
  archived: ArchivedMode;
  /** Les tris demandes. Vide = ordre naturel de Firestore. */
  order: ListOrder[];
  /** Taille de page, ou null. */
  limit: number | null;
  /** Identifiant du curseur, ou null. */
  after: string | null;
}

/**
 * Resout le mode d'archives d'un jeu d'options. Fonction PURE, un seul endroit.
 *
 * `archived` gagne sur `includeArchived` quand les deux sont fournis (cf. le
 * JSDoc de `ListOptions.archived`). Sans `archived`, l'ancien drapeau garde son
 * sens EXACT : `true` vaut `'all'`, tout le reste vaut `'exclude'`.
 */
function resolveArchivedMode(options: ListOptions): ArchivedMode {
  if (options.archived !== undefined) {
    if (
      options.archived !== 'exclude' &&
      options.archived !== 'only' &&
      options.archived !== 'all'
    ) {
      throw new Error(
        `planListQuery : \`archived\` doit valoir 'exclude', 'only' ou 'all' (recu : ${String(options.archived)}). ` +
          "Une valeur inconnue ne peut pas se rabattre sur un defaut : selon le mode voulu, l'ecran " +
          'montrerait la corbeille a la place de la liste, ou une liste vide sans erreur.',
      );
    }
    return options.archived;
  }
  return options.includeArchived === true ? 'all' : 'exclude';
}

/**
 * Les cles que `where` ne peut pas porter : chacune a deja son option ou son
 * cadrage dans ce module (cf. le JSDoc de `ListOptions.where`).
 */
const CLES_RESERVEES = new Set(['orgId', 'type', 'archivedAt', 'deletedAt']);

/** Les clefs que `where` ne porte pas sur une famille a acces par fiche : l'option `acces` les pose. */
const CLES_D_ACCES = (field: string) => field === 'acces' || field.startsWith('acces.') || field === 'conversationId';

/**
 * Traduit `where` en filtres d'egalite. Fonction PURE, un seul endroit.
 *
 * L'ordre rendu est celui des cles de l'objet, donc celui que l'appelant a
 * ecrit. Consequence a connaitre avant de la decouvrir dans
 * `firebase/firestore.indexes.json` : deux ecrans qui posent les MEMES egalites
 * dans un ordre different obtiennent deux blocs d'index differents pour le meme
 * jeu de champs. Rangez les cles dans le meme ordre plutot que de deployer deux
 * index qui font le meme travail.
 */
function equalityFilters(egalites: unknown, cadrage?: CadrageAcces): ListPlan['filters'] {
  if (typeof egalites !== 'object' || egalites === null || Array.isArray(egalites)) {
    throw new Error(
      'planListQuery : `where` doit etre un objet de champs a comparer, ' +
        `par exemple { assetId: 'a1' } (recu : ${String(egalites)}). ` +
        'Un objet vide est admis : il ne pose simplement aucun filtre.',
    );
  }

  const filters: ListPlan['filters'] = [];
  for (const [field, value] of Object.entries(egalites)) {
    if (field.length === 0) {
      throw new Error('planListQuery : `where` doit designer des champs non vides.');
    }
    if (CLES_RESERVEES.has(field)) {
      throw new Error(
        `planListQuery : \`where\` ne peut pas porter "${field}". Ce champ a deja son option ` +
          '(`type`, `archived`) ou son cadrage (`orgId`, et `deletedAt` dont le SDK se charge). ' +
          'Deux filtres sur le meme champ rendent zero ligne SANS la moindre erreur : ' +
          "l'ecran afficherait \"aucun element\" et personne ne chercherait du cote du code.",
      );
    }
    if (cadrage !== undefined && CLES_D_ACCES(field)) {
      throw new Error(
        `planListQuery : \`where\` ne peut pas porter "${field}" sur une famille a acces par fiche : ` +
          "c'est l'option `acces` qui le pose ('equipe', 'prive', { ancre }, { conversation }), " +
          'et les rules refusent une requete qui ne le fixe pas comme elles l\'attendent.',
      );
    }
    if (value === undefined) {
      throw new Error(
        `planListQuery : \`where.${field}\` vaut undefined. C'est une variable oubliee, ` +
          'jamais un filtre : retirez la cle pour ne pas filtrer, ou donnez sa valeur. ' +
          'Pour chercher un champ vide, `null` dit "connu et vide" (il ne rattrape pas un champ ABSENT).',
      );
    }
    filters.push({ field, value });
  }
  return filters;
}

/** Normalise la forme courte (`'name'`) et la forme longue d'un tri. */
function normalizeOrder(order: string | ListOrder): ListOrder {
  const field = typeof order === 'string' ? order : order.field;
  if (typeof field !== 'string' || field.length === 0) {
    throw new Error(
      'planListQuery : `orderBy` doit designer un champ non vide. Un champ vide ne ' +
        'leve pas chez Firestore au moment ou on le pose, il leve plus tard, ' +
        'avec un message qui ne parle plus de tri.',
    );
  }
  const dir = typeof order === 'string' ? 'asc' : (order.dir ?? 'asc');
  return { field, dir };
}

/**
 * Traduit les options en plan de requete. Fonction PURE : aucune dependance
 * Firestore, donc testable seule.
 *
 * L'ordre des filtres est fige et volontaire : `orgId` D'ABORD. Les Security
 * Rules refusent en bloc toute requete `list` qui n'est pas cadree par l'org de
 * l'appelant ; ce cadrage n'est pas une option d'appel, c'est la condition pour
 * que la requete existe.
 */
export function planListQuery(
  orgId: string,
  options: ListOptions = {},
  cadrage?: CadrageAcces,
): ListPlan {
  if (typeof orgId !== 'string' || orgId.length === 0) {
    throw new Error(
      'planListQuery : orgId manquant. Toute requete `list` doit etre cadree par ' +
        "l'organisation, les Security Rules refusent les requetes globales.",
    );
  }

  const filters: ListPlan['filters'] = [{ field: 'orgId', value: orgId }];
  const inequalities: ListPlan['inequalities'] = [];
  const contains: ListPlan['contains'] = [];
  let vide = false;
  const archived = resolveArchivedMode(options);

  // Le sous-type metier, quand la famille en porte un.
  if (options.type !== undefined) {
    if (typeof options.type !== 'string' || options.type.length === 0) {
      throw new Error('planListQuery : `type` doit etre une chaine non vide quand il est fourni.');
    }
    filters.push({ field: 'type', value: options.type });
  }

  // Suppression logique du socle. Le SDK ecrit `archivedAt: null` a la creation
  // et les Security Rules l'EXIGENT : tout document ecrit par un client porte
  // donc le champ. Piege a connaitre quand meme : un document importe par
  // l'Admin SDK sans ce champ serait invisible de toutes les listes, parce que
  // `== null` ne rattrape pas un champ ABSENT. Un import qui oublie
  // `archivedAt` ne produit pas d'erreur, il produit des lignes fantomes.
  //
  // Le mode `'only'` (les Archives) pose une INEGALITE, et elle a exactement le
  // meme angle mort, en pire : `!= null` ne rattrape pas davantage un champ
  // absent, donc un document sans `archivedAt` n'apparait NI dans la liste NI
  // dans les archives. Il n'est nulle part.
  if (archived === 'exclude') {
    filters.push({ field: 'archivedAt', value: null });
  } else if (archived === 'only') {
    inequalities.push({ field: 'archivedAt', op: '!=', value: null });
  }

  // L'ACCES PAR FICHE, apres le cadrage de la porte et avant les egalites de
  // l'appelant : c'est ce que les rules lisent, dans cet ordre, et c'est l'ordre
  // des index declares pour ces familles (`firebase/firestore.indexes.json`).
  const acces = accesFilters(options.acces, cadrage);
  filters.push(...acces.filters);
  contains.push(...acces.contains);
  vide = acces.vide;

  // Les egalites de l'appelant, APRES le cadrage et jamais a sa place : un lien
  // entre portes ("les interventions de CE materiel") est un filtre de plus.
  // L'ordre compte, parce que c'est celui du bloc d'index rendu plus bas.
  if (options.where !== undefined) {
    filters.push(...equalityFilters(options.where, cadrage));
  }

  const order = options.orderBy != null ? [normalizeOrder(options.orderBy)] : [];

  let limit: number | null = null;
  if (options.limit !== undefined) {
    if (!Number.isInteger(options.limit) || options.limit <= 0) {
      throw new Error(
        `planListQuery : \`limit\` doit etre un entier strictement positif (recu : ${String(options.limit)}).`,
      );
    }
    if (options.limit > MAX_LIST_LIMIT) {
      throw new Error(
        `planListQuery : \`limit\` plafonne a ${MAX_LIST_LIMIT} (recu : ${String(options.limit)}). ` +
          'Pour lire davantage, enchainez les pages avec `after` (le curseur), ' +
          'jamais en agrandissant la page. Retirer `limit` ne corrige rien : ' +
          'sans limite, la requete lit la collection entiere, ce que ce module existe pour eviter.',
      );
    }
    limit = options.limit;
  }

  // Le curseur est interpole dans une reference de document : une chaine vide
  // ou porteuse d'un slash produit une erreur Firestore sur le nombre de
  // segments de chemin, a mille lieues de la cause. L'idiome
  // `after: derniere?.id ?? ''` est trop courant pour ne pas etre attrape ici.
  let after: string | null = null;
  if (options.after !== undefined && options.after !== null) {
    if (typeof options.after !== 'string' || options.after.length === 0) {
      throw new Error(
        'planListQuery : `after` doit etre un identifiant de document non vide. ' +
          "Pour demander la premiere page, omettez `after` (ou passez null), n'envoyez pas une chaine vide.",
      );
    }
    if (options.after.includes('/')) {
      throw new Error(
        `planListQuery : \`after\` doit etre un IDENTIFIANT de document, pas un chemin (recu : "${options.after}").`,
      );
    }
    after = options.after;
  }

  return { filters, inequalities, contains, vide, archived, order, limit, after };
}

/**
 * Traduit l'option `acces` en filtres, selon le role de la famille. Fonction
 * PURE, un seul endroit : c'est la promesse faite aux rules, prouvee par
 * `packages/sdk/tests/list-query.test.mjs`.
 *
 *   porteuse  'equipe'          -> acces.visibilite == 'equipe'
 *   porteuse  'prive'           -> acces.visibilite == 'prive'
 *                                  + acces.participants array-contains personId
 *   porteuse  { ancre }         -> acces.visibilite == 'ancre'
 *                                  + acces.ancre.family == f + acces.ancre.recordId == id
 *   heritiere 'equipe'          -> acces.visibilite == 'equipe'
 *   heritiere { conversation }  -> conversationId == id (les rules relisent la conversation)
 *
 * Sans cadrage (famille sans acces par fiche), aucune option n'est admise.
 */
function accesFilters(
  acces: AccesDeListe | undefined,
  cadrage: CadrageAcces | undefined,
): Pick<ListPlan, 'filters' | 'contains' | 'vide'> {
  if (cadrage === undefined) {
    if (acces !== undefined) {
      throw new Error(
        "planListQuery : l'option `acces` ne vaut que sur une famille a acces par fiche " +
          '(`FAMILLES_ACCES_PAR_FICHE`, @taki/data-model). Sur les autres, la porte decide seule.',
      );
    }
    return { filters: [], contains: [], vide: false };
  }
  const demande = acces ?? 'equipe';
  if (demande === 'equipe') {
    return { filters: [{ field: 'acces.visibilite', value: 'equipe' }], contains: [], vide: false };
  }
  if (demande === 'prive') {
    if (cadrage.role !== 'porteuse') {
      throw new Error(
        "planListQuery : `acces: 'prive'` ne vaut que sur une famille porteuse (`contents`, `conversations`). " +
          'Les messages se listent par conversation : `acces: { conversation: id }`.',
      );
    }
    if (cadrage.personId === null || cadrage.personId.length === 0) {
      return { filters: [{ field: 'acces.visibilite', value: 'prive' }], contains: [], vide: true };
    }
    return {
      filters: [{ field: 'acces.visibilite', value: 'prive' }],
      contains: [{ field: 'acces.participants', value: cadrage.personId }],
      vide: false,
    };
  }
  if (typeof demande === 'object' && demande !== null && 'ancre' in demande) {
    const { ancre } = demande;
    if (cadrage.role !== 'porteuse') {
      throw new Error(
        'planListQuery : `acces: { ancre }` ne vaut que sur une famille porteuse. ' +
          'Les messages se listent par conversation : `acces: { conversation: id }`.',
      );
    }
    if (
      typeof ancre !== 'object' || ancre === null ||
      typeof ancre.family !== 'string' || ancre.family.length === 0 ||
      typeof ancre.recordId !== 'string' || ancre.recordId.length === 0
    ) {
      throw new Error('planListQuery : `acces.ancre` attend `{ family, recordId }`, deux chaines non vides.');
    }
    return {
      filters: [
        { field: 'acces.visibilite', value: 'ancre' },
        { field: 'acces.ancre.family', value: ancre.family },
        { field: 'acces.ancre.recordId', value: ancre.recordId },
      ],
      contains: [],
      vide: false,
    };
  }
  if (typeof demande === 'object' && demande !== null && 'conversation' in demande) {
    if (cadrage.role !== 'heritiere') {
      throw new Error(
        'planListQuery : `acces: { conversation }` ne vaut que sur une famille heritiere (`messages`). ' +
          "Une conversation se lit par `sdk.conversations.get(id)`.",
      );
    }
    if (typeof demande.conversation !== 'string' || demande.conversation.length === 0) {
      throw new Error('planListQuery : `acces.conversation` attend un id de conversation non vide.');
    }
    return { filters: [{ field: 'conversationId', value: demande.conversation }], contains: [], vide: false };
  }
  throw new Error(
    `planListQuery : \`acces\` doit valoir 'equipe', 'prive', { ancre } ou { conversation } (recu : ${JSON.stringify(demande)}).`,
  );
}

/**
 * Le bloc a coller dans `firebase/firestore.indexes.json` pour un plan donne,
 * ou `null` quand Firestore sait deja repondre sans index declare (le cas des
 * requetes a filtres d'egalite seuls).
 *
 * Sert deux fois : a produire le fichier d'index, et a enrichir le message
 * d'erreur quand une requete part sans son index (voir `runList`). Un
 * `failed-precondition` brut de Firestore est un lien vers une console : ici on
 * veut le JSON, parce que l'index vit dans le depot.
 *
 * LES CLES DE `where` SONT DES EGALITES COMME LES AUTRES : elles entrent dans
 * le bloc avec `orgId`, `type` et `archivedAt`, dans l'ordre du plan. Une
 * egalite de plus n'exige donc RIEN a elle seule (les index a champ unique
 * suffisent), mais des qu'un `orderBy` porte sur un autre champ, le bloc rendu
 * la porte : un lien entre portes trie par date est exactement ce cas.
 */
export function describeMissingIndex(
  collectionName: string,
  plan: ListPlan,
): FirestoreIndex | null {
  if (plan.order.length === 0 && plan.inequalities.length === 0) return null;

  // Un tri sur un champ DEJA filtre en egalite n'exige aucun index composite
  // (l'egalite fige la valeur, il n'y a plus rien a ordonner). Le declarer
  // quand meme produirait un bloc avec deux fois le meme `fieldPath`, que
  // Firestore refuse comme definition d'index : on aurait remplace une erreur
  // par une autre, plus obscure.
  const equalityFields = new Set(plan.filters.map((filter) => filter.field));
  const explicitSorts = plan.order.filter((entry) => !equalityFields.has(entry.field));

  // Un champ filtre par INEGALITE est un champ ORDONNE pour Firestore : le mode
  // Archives (`archivedAt != null`) exige donc un index composite MEME sans
  // `orderBy`, la ou les egalites seules n'en exigeaient aucun. Deux details
  // qui decident si le bloc rendu marche du premier coup : le tri implicite se
  // place APRES les tris explicites, et il prend le SENS du dernier d'entre eux
  // (c'est ce que fait `queryNormalizedOrderBy` dans firebase-js-sdk, et c'est
  // la requete reellement envoyee qui decide de l'index, pas celle qu'on a
  // ecrite). Un champ deja trie explicitement, ou deja fige par une egalite,
  // n'est pas repris une deuxieme fois : Firestore refuse un index qui porte
  // deux fois le meme `fieldPath`.
  const lastExplicit = explicitSorts[explicitSorts.length - 1];
  const implicitDir: ListDirection = lastExplicit?.dir === 'desc' ? 'desc' : 'asc';
  const explicitFields = new Set(explicitSorts.map((entry) => entry.field));
  const implicitSorts: ListOrder[] = plan.inequalities
    .filter((filter) => !equalityFields.has(filter.field) && !explicitFields.has(filter.field))
    .map((filter) => ({ field: filter.field, dir: implicitDir }));

  const sorts = [...explicitSorts, ...implicitSorts];
  if (sorts.length === 0) return null;

  const last = sorts[sorts.length - 1] as ListOrder;
  const lastOrder: 'ASCENDING' | 'DESCENDING' = last.dir === 'desc' ? 'DESCENDING' : 'ASCENDING';

  const fields: FirestoreIndex['fields'] = [
    ...plan.filters.map((filter) => ({ fieldPath: filter.field, order: 'ASCENDING' as const })),
    // Le `array-contains` d'une liste privee, APRES les egalites et AVANT les
    // tris : c'est la place que Firestore lui donne dans un index composite.
    ...plan.contains.map((filter) => ({ fieldPath: filter.field, arrayConfig: 'CONTAINS' as const })),
    ...sorts.map((entry) => ({
      fieldPath: entry.field,
      order: (entry.dir === 'desc' ? 'DESCENDING' : 'ASCENDING') as 'ASCENDING' | 'DESCENDING',
    })),
    // `__name__` EXPLICITE, dans le sens du dernier tri. Firestore ajoute tout
    // seul ce departage a la requete, mais un index declare sans lui est
    // complete en ASCENDING : sur un tri DESCENDING, l'index ne correspond alors
    // a rien et l'erreur revient apres le deploiement. Le bloc rendu ici est
    // celui qu'on colle, il doit marcher du premier coup.
    { fieldPath: '__name__', order: lastOrder },
  ];

  return { collectionGroup: collectionName, queryScope: 'COLLECTION', fields };
}

/**
 * Relit le document du curseur, SANS converter (on n'a besoin que de sa
 * position, pas de sa validation Zod : un document mal forme ailleurs dans la
 * collection ne doit pas casser la page demandee).
 *
 * Par une REQUETE cadree par `orgId` et `type`, pour conserver les droits de
 * la liste. Si elle rend zero document, on peut nommer `CursorLostError`.
 * Les rules actuelles peuvent toutefois evaluer `resource.data` sur un id
 * absent et refuser la requete avant qu'elle rende zero resultat. Ce
 * `permission-denied` est conserve : le transformer en curseur perdu pourrait
 * masquer une vraie perte de droits. Le test contre emulateur compare le
 * verdict de `list` et de `observeList` sur ce cas.
 */
async function readCursorDoc(
  raw: CollectionReference<DocumentData>,
  plan: ListPlan,
  cursorId: string,
): Promise<QueryDocumentSnapshot<DocumentData>> {
  // Les Rules peuvent exiger `type == ...` pour une derogation par porte. La
  // relecture du curseur reprend donc le cadrage de permissions de la liste.
  const scope = cursorPermissionFilters(plan);
  const found = await getDocs(
    query(
      raw,
      ...scope.map((filter) => where(filter.field, '==', filter.value)),
      ...plan.contains.map((filter) => where(filter.field, 'array-contains', filter.value)),
      where(documentId(), '==', cursorId),
      limitTo(1),
    ),
  );
  const first = found.docs[0];
  if (first === undefined) {
    throw new CursorLostError(raw.id, cursorId, 'il n\'existe plus, ou il a quitte cette organisation');
  }
  return first;
}

/**
 * Cadrage minimal que les Rules doivent voir lors de la relecture du curseur :
 * l'org, le type (derogations par porte) et, sur une famille a acces par fiche,
 * ce que l'option `acces` a pose (`acces.*`, `conversationId`) : sans lui, la
 * relecture serait refusee en bloc comme toute liste qui ne le fixe pas.
 */
export function cursorPermissionFilters(plan: ListPlan): ListPlan['filters'] {
  return plan.filters.filter(({ field }) => field === 'orgId' || field === 'type' || field === 'createdBy' || CLES_D_ACCES(field));
}

/**
 * Traduit un plan en contraintes Firestore. Le curseur est resolu ici (une
 * lecture), d'ou l'asynchronisme.
 */
async function toConstraints(
  col: CollectionReference<DocumentData>,
  orgId: string,
  plan: ListPlan,
): Promise<QueryConstraint[]> {
  const constraints: QueryConstraint[] = plan.filters.map((filter) =>
    where(filter.field, '==', filter.value),
  );

  // Le `array-contains` d'une liste privee, puis les inegalites APRES les
  // egalites : c'est l'ordre dans lequel Firestore veut les champs de l'index
  // composite, et le bloc rendu par `describeMissingIndex` suit le meme ordre.
  for (const filter of plan.contains) {
    constraints.push(where(filter.field, 'array-contains', filter.value));
  }
  for (const filter of plan.inequalities) {
    constraints.push(where(filter.field, filter.op, filter.value));
  }

  for (const entry of plan.order) {
    constraints.push(orderByField(entry.field, entry.dir ?? 'asc'));
  }

  if (plan.after != null) {
    const cursorDoc = await readCursorDoc(collection(col.firestore, col.path), plan, plan.after);
    try {
      constraints.push(startAfter(cursorDoc));
    } catch (cause) {
      // Firestore refuse un curseur dont le document ne PORTE PAS le champ
      // trie ("you are trying to start ... using a document for which the
      // field X does not exist"). Arrive sur un champ facultatif dont la valeur
      // a ete effacee apres la lecture de la page precedente. C'est un curseur
      // inutilisable, pas une panne : meme reprise que le curseur disparu.
      throw new CursorLostError(
        col.id,
        plan.after,
        'il ne porte plus le champ sur lequel la liste est triee',
        { cause },
      );
    }
  }

  if (plan.limit != null) {
    constraints.push(limitTo(plan.limit));
  }

  return constraints;
}

/**
 * Construit la requete Firestore d'une liste. `observeList` s'abonne a cette
 * meme requete : cadrage, curseur et modes d'archives restent ceux de `list`.
 */
export async function buildListQuery<T>(
  col: CollectionReference<T>,
  orgId: string,
  options: ListOptions = {},
  cadrage?: CadrageAcces,
): Promise<Query<T>> {
  const plan = cadrerParAuteur(planListQuery(orgId, options, cadrage), await auteurPourRequete(col.firestore, orgId, col.id, options.type));
  const constraints = await toConstraints(
    col as unknown as CollectionReference<DocumentData>,
    orgId,
    plan,
  );
  return query(col, ...constraints);
}

/**
 * EXECUTE une liste : c'est ce que les 56 repositories appellent, et la seule
 * chose qu'ils appellent. Une famille n'a plus rien a decider sur le cadrage,
 * les archives ou la pagination.
 *
 * Le tableau rendu est celui de la page demandee, dans l'ordre du serveur. Il
 * n'y a pas de type de retour "page + curseur" : le curseur de la page suivante
 * est l'identifiant du DERNIER element rendu, et "il reste des pages" se lit
 * "la page est pleine" (`rendus === limit`). Une page finale exactement pleine
 * fait donc afficher un "charger la suite" qui rend une page vide : c'est le
 * defaut connu de ce schema, il se corrige tout seul au clic suivant, et il
 * evite un type de retour de plus dans 56 interfaces.
 */
export async function runList<T>(
  col: CollectionReference<T>,
  orgId: string,
  options: ListOptions = {},
  cadrage?: CadrageAcces,
): Promise<T[]> {
  const plan = cadrerParAuteur(planListQuery(orgId, options, cadrage), await auteurPourRequete(col.firestore, orgId, col.id, options.type));
  // Une liste privee sans `personId` : rien ne peut etre partage a l'appelant,
  // et les rules refuseraient la requete. Vide, sans rien demander.
  if (plan.vide) return [];
  try {
    const fetchPage = async (after: string | null, pageLimit: number | null) => {
      const pagePlan = { ...plan, after, limit: pageLimit };
      const constraints = await toConstraints(
        col as unknown as CollectionReference<DocumentData>,
        orgId,
        pagePlan,
      );
      const snap = await getDocs(query(col, ...constraints));
      return snap.docs.map((d) => ({ id: d.id, value: d.data() }));
    };

    const first = await fetchPage(plan.after, plan.limit);
    const rows = first.map(({ value }) => value);
    // Mode 'exclude' : on rend la page telle quelle. AUCUN travail
    // supplementaire sur le chemin de loin le plus frequent (toutes les listes
    // de toutes les apps) : un document supprime porte `archivedAt`, il est
    // donc deja sorti cote SERVEUR par `where('archivedAt', '==', null)`.
    if (plan.archived === 'exclude') return rows;
    // Modes 'only' et 'all' : les supprimes portent `archivedAt`, le serveur
    // les a donc rendus. On les retire ici et on complete une page limitee.
    return fillVisiblePage(first, plan.limit, fetchPage);
  } catch (cause) {
    throw enrichIndexError(col.id, plan, cause);
  }
}

type PageEntry<T> = { id: string; value: T };

/**
 * Remplit une page apres retrait des supprimes. Les lectures suivantes partent
 * du dernier document vu par le serveur, ce qui garantit une progression meme
 * quand une tranche ne contient que des documents supprimes.
 */
export async function fillVisiblePage<T>(
  first: PageEntry<T>[],
  requestedLimit: number | null,
  fetchPage: (after: string | null, limit: number | null) => Promise<PageEntry<T>[]>,
): Promise<T[]> {
  const visible = first.filter(({ value }) => !isDeleted(value)).map(({ value }) => value);
  if (requestedLimit === null) return visible;

  let batch = first;
  while (visible.length < requestedLimit && batch.length === requestedLimit) {
    const after = batch.at(-1)?.id;
    if (after === undefined) break;
    batch = await fetchPage(after, requestedLimit);
    for (const { value } of batch) {
      if (!isDeleted(value)) visible.push(value);
      if (visible.length === requestedLimit) break;
    }
  }
  return visible;
}

/**
 * Le document est-il SUPPRIME (`deletedAt` pose) ?
 *
 * Le cast local et etroit est volontaire : `deletedAt` vit sur le SOCLE
 * (`baseEntityFields`, @taki/data-model), pas sur le `T` generique de ce module,
 * qui ne connait aucune famille et ne doit surtout pas se mettre a en dependre.
 * On ne lit qu'une cle, on ne suppose rien d'autre de la forme de `T`, et un
 * document historique sans le champ vaut `undefined`, donc NON supprime : c'est
 * exactement le sens voulu.
 */
function isDeleted(row: unknown): boolean {
  return (row as { deletedAt?: unknown } | null)?.deletedAt != null;
}

/**
 * COMPTER sans charger. Le pendant indispensable de la pagination : des qu'un
 * ecran ne charge plus qu'une page, tout compteur calcule sur ce qu'il a en
 * memoire (le "47" d'une fiche, les stats de la zone 3, le compteur du menu)
 * se met a MENTIR, et il ment sans rien signaler. Une liste paginee dont les
 * compteurs comptent la page courante est pire qu'une liste non paginee.
 *
 * `getCountFromServer` compte cote serveur et se facture une lecture par
 * tranche de 1000 documents, pas une par document : c'est ce qui rend un total
 * juste abordable sur un annuaire de plusieurs milliers de lignes.
 *
 * Le compte se fait sur le MEME cadrage que la liste (org, archives, sous-type,
 * egalites de `where`), mais SANS `limit`, `after` ni `orderBy`, que le type
 * refuse (cf. `CountOptions`) : compter une page n'a pas de sens, et un tri ne
 * change pas un total (il ne ferait qu'exiger un index). `runSum` et
 * `runAverage` partagent exactement ce cadrage, par `aggregateFilters`.
 *
 * LES SUPPRIMES NE SONT PAS DEDANS, meme en `'only'` et en `'all'` (09/09/2026).
 * `getCountFromServer` ne rend qu'un nombre : il n'y a rien a filtrer au retour,
 * donc le filtre est pose DANS la requete (`deletedAt == null`,
 * `aggregateFilters`), et non apres coup comme dans `runList`. Le total et la
 * liste portent ainsi sur le meme jeu. Le mode `'exclude'` etait deja exact et
 * ne gagne pas l'egalite : un supprime porte `archivedAt`, il en etait deja
 * sorti.
 */
export async function runCount<T>(
  col: CollectionReference<T>,
  orgId: string,
  options: CountOptions = {},
  cadrage?: CadrageAcces,
): Promise<number> {
  const { plan, q } = await aggregateQuery(col, orgId, options, cadrage);
  if (plan.vide) return 0;
  try {
    const snap = await getCountFromServer(q);
    return snap.data().count;
  } catch (cause) {
    // Meme enrichissement que `runList`. Ce n'est plus seulement une precaution
    // depuis le mode Archives : un compte n'a jamais de tri, mais il peut porter
    // une INEGALITE (`archivedAt != null`), et une inegalite exige un index
    // composite. Sans cet enrichissement, le compteur du mode Archives d'une
    // famille non declaree rendrait un `failed-precondition` brut, sans le bloc
    // JSON a coller.
    throw enrichIndexError(col.id, plan, cause);
  }
}

/**
 * Les filtres d'un AGREGAT : les egalites du plan, `deletedAt == null` quand le
 * mode d'archives ne l'a pas deja exclu, puis les inegalites. C'est exactement
 * le cadrage de la liste, MOINS sa fenetre (tri, page, curseur).
 *
 * LE FILTRE DES SUPPRIMES EST ICI, ET NULLE PART DANS LA LISTE. C'est le seul
 * ecart entre les deux cadrages, et il les fait justement se REJOINDRE : la
 * liste retire les supprimes COTE CLIENT, apres lecture (`runList`), alors
 * qu'une agregation ne rend qu'un nombre et n'a rien a filtrer au retour. Sans
 * cette egalite, `count` en `'only'` et en `'all'` depassait la liste
 * d'exactement la taille de la corbeille. En `'exclude'` (le defaut), elle
 * serait un doublon : un supprime porte `archivedAt`, il est deja hors du
 * compte, et une egalite de plus ne ferait qu'ajouter un champ a l'index.
 *
 * ELLE NE PASSE PAS DANS LA LISTE, et c'est deliberent : `runList` fenetre et
 * PAGINE, donc la meme egalite y ferait disparaitre tout document ou la clef
 * `deletedAt` est ABSENTE (le piege du champ absent, en tete de ce module), y
 * compris ceux d'avant le chantier des trois etats. Un agregat, lui, ne rend
 * qu'un total : le voir baisser se lit, une ligne qui manque, non.
 *
 * Fonction PURE et exportee pour la meme raison que `planListQuery` : c'est ce
 * qui rend prouvable, par un test unitaire et sans emulateur, que `count`,
 * `sum` et `average` comptent, somment et moyennent sur le MEME jeu de
 * documents que la liste. Trois copies de cette traduction auraient fini par
 * repondre trois chiffres qui ne parlent pas de la meme chose.
 */
export function aggregateFilters(
  plan: ListPlan,
): Array<{ field: string; op: '==' | '!='; value: unknown }> {
  return [
    ...plan.filters.map((filter) => ({
      field: filter.field,
      op: '==' as const,
      value: filter.value,
    })),
    // Les supprimes, sortis au SERVEUR, avec les autres egalites : en `'all'`
    // la requete n'a alors QUE des egalites, donc aucun index composite
    // nouveau. En `'only'`, elle porte deja l'inegalite du mode Archives et
    // son index existant gagne un champ d'egalite de plus.
    ...(plan.archived === 'exclude'
      ? []
      : [{ field: 'deletedAt', op: '==' as const, value: null }]),
    // Les inegalites APRES les egalites, comme dans `toConstraints` : c'est
    // l'ordre des champs de l'index composite.
    ...plan.inequalities,
  ];
}

/**
 * Le plan et la requete d'un agregat, construits UNE fois pour les trois
 * verbes. La politique et le role sont relus avant de construire la requete ;
 * un agregat n'a ni curseur ni page.
 */
async function aggregateQuery<T>(
  col: CollectionReference<T>,
  orgId: string,
  options: CountOptions,
  cadrage?: CadrageAcces,
): Promise<{ plan: ListPlan; q: Query<T> }> {
  const plan = cadrerParAuteur(planListQuery(orgId, options, cadrage), await auteurPourRequete(col.firestore, orgId, col.id, options.type));
  const constraints: QueryConstraint[] = aggregateFilters(plan).map((filter) =>
    where(filter.field, filter.op, filter.value),
  );
  // Le `array-contains` d'un agregat prive : le meme jeu de documents que la
  // liste, par la meme contrainte.
  for (const filter of plan.contains) {
    constraints.push(where(filter.field, 'array-contains', filter.value));
  }
  return { plan: aggregatePlan(plan), q: query(col, ...constraints) };
}

/**
 * LE PLAN D'UN AGREGAT, PAS CELUI DE LA LISTE, et il ne sert qu'a decrire
 * l'index (`enrichIndexError`, `describeAggregateIndex`). Depuis que l'agregat
 * porte une egalite de plus (`deletedAt == null` hors `'exclude'`), rendre le
 * plan de la LISTE ferait coller un bloc JSON auquel ce champ manque : l'index
 * deploye ne repondrait pas a la requete, et la meme erreur reviendrait apres le
 * deploiement. En `'exclude'`, les deux plans sont identiques.
 */
function aggregatePlan(plan: ListPlan): ListPlan {
  return {
    ...plan,
    filters: aggregateFilters(plan)
      .filter((filter) => filter.op === '==')
      .map((filter) => ({ field: filter.field, value: filter.value })),
  };
}

/**
 * L'INDEX COMPOSITE D'UN AGREGAT, ou `null` quand Firestore n'en exige aucun.
 *
 * UNE SOMME ET UNE MOYENNE EXIGENT TOUJOURS UN INDEX, meme sur des egalites
 * seules, et c'est le piege qui a casse la carte « Heures » de Timesheet en
 * production le 04/10/2026 : la liste et le compte de la carte passaient (des
 * egalites seules se servent des index a champ unique), la somme du pied non.
 * Firestore veut le champ agrege DANS l'index, apres les egalites et les
 * inegalites, en ASCENDING, puis `__name__` ASCENDING. Forme relevee sur le
 * message d'erreur de Firestore lui-meme, pas deduite.
 *
 * UN COMPTE n'en exige que s'il porte une inegalite (le mode Archives) : c'est
 * alors la forme de `describeMissingIndex`, sans tri explicite.
 *
 * Les egalites peuvent figurer dans n'importe quel ordre dans l'index deploye :
 * Firestore les apparie comme un ensemble. Le bloc rendu suit l'ordre du plan.
 *
 * Fonction PURE et exportee : c'est elle que lit `scripts/check-index-composites.mjs`
 * pour prouver, en CI, que chaque agregat declare par une app a son index.
 */
export function describeAggregateIndex(
  collectionName: string,
  orgId: string,
  options: CountOptions = {},
  field?: string,
  cadrage?: CadrageAcces,
): FirestoreIndex | null {
  const plan = aggregatePlan(planListQuery(orgId, options, cadrage));
  if (field === undefined) return describeMissingIndex(collectionName, { ...plan, order: [] });
  const champ = aggregateField(field, 'describeAggregateIndex');
  const egalites = new Set(plan.filters.map((filter) => filter.field));
  const inegalites = plan.inequalities.filter((filter) => !egalites.has(filter.field));
  return {
    collectionGroup: collectionName,
    queryScope: 'COLLECTION',
    fields: [
      ...plan.filters.map((filter) => ({ fieldPath: filter.field, order: 'ASCENDING' as const })),
      ...plan.contains.map((filter) => ({ fieldPath: filter.field, arrayConfig: 'CONTAINS' as const })),
      ...inegalites.map((filter) => ({ fieldPath: filter.field, order: 'ASCENDING' as const })),
      { fieldPath: champ, order: 'ASCENDING' },
      { fieldPath: '__name__', order: 'ASCENDING' },
    ],
  };
}

/**
 * Le champ d'un agregat, verifie AVANT de toucher Firestore.
 *
 * Un champ vide ne leve pas la ou on l'ecrit : il part au serveur et revient en
 * erreur d'agregation, avec un message qui ne parle plus ni de somme ni de
 * moyenne. L'idiome qui l'amene est toujours le meme, `sum(porte.champStat ??
 * '')`.
 */
function aggregateField(field: string, verbe: string): string {
  if (typeof field !== 'string' || field.length === 0) {
    throw new Error(
      `${verbe} : le champ a agreger doit etre une chaine non vide (recu : ${String(field)}). ` +
        'Un champ facultatif absent ne se somme pas sur "" : ne demandez pas l\'agregat.',
    );
  }
  return field;
}

/**
 * SOMMER sans charger. Meme cadrage et memes options que `count`, meme
 * facturation : une lecture par tranche de 1000 entrees d'index, pas une par
 * document. C'est ce qui rend un total d'heures ou de montants juste sur une
 * collection que l'ecran ne charge qu'une page a la fois.
 *
 * REND `0` QUAND AUCUN DOCUMENT NE PASSE LE FILTRE, et c'est le bon zero : une
 * somme de rien vaut zero. Les documents ou le champ est ABSENT ou n'est pas un
 * nombre sont simplement ignores par Firestore, pas comptes comme zero : une
 * somme peut donc porter sur moins de documents que `count` n'en compte, sans
 * qu'aucune erreur ne le dise. Sur un champ facultatif, c'est la moyenne qui le
 * trahit, jamais la somme.
 *
 * LE MEME CADRAGE QUE `count`, `deletedAt == null` compris : les documents
 * SUPPRIMES n'entrent dans aucun des trois agregats, sur aucun mode d'archives
 * (cf. `aggregateFilters` et `runCount`).
 */
export async function runSum<T>(
  col: CollectionReference<T>,
  orgId: string,
  field: string,
  options: CountOptions = {},
  cadrage?: CadrageAcces,
): Promise<number> {
  const champ = aggregateField(field, 'runSum');
  const { plan, q } = await aggregateQuery(col, orgId, options, cadrage);
  if (plan.vide) return 0;
  try {
    const snap = await getAggregateFromServer(q, { valeur: sumOf(champ) });
    return snap.data().valeur;
  } catch (cause) {
    throw enrichIndexError(col.id, plan, cause, describeAggregateIndex(col.id, orgId, { ...options, where: { ...options.where, ...Object.fromEntries(plan.filters.filter(f => f.field === 'createdBy').map(f => [f.field, f.value])) } }, champ, cadrage));
  }
}

/**
 * MOYENNER sans charger. Memes options, meme cadrage et meme facturation que
 * `runSum`.
 *
 * LE DIVISEUR N'EST PAS `count()`, et c'est le piege du champ facultatif :
 * Firestore moyenne sur les documents qui PORTENT le champ, pas sur ceux que le
 * filtre retient. Trois fiches retenues dont deux portent 30 et 90 donnent 60,
 * jamais 40. Un ecran qui affiche la moyenne a cote du compteur montre donc deux
 * chiffres qui ne parlent pas du meme jeu de fiches.
 *
 * REND `null` QUAND AUCUN DOCUMENT NE PASSE LE FILTRE, et ce null ne se
 * transforme pas en zero : c'est ce que rend Firestore, et c'est la verite. Une
 * moyenne de rien n'est pas zero (un materiel sans intervention n'a pas un cout
 * moyen de 0 euro), et l'ecran qui affiche l'un a la place de l'autre annonce
 * un chiffre que personne n'a mesure. Le repli, si repli il y a, se decide a
 * l'affichage, ou l'on sait ecrire "aucune donnee".
 */
export async function runAverage<T>(
  col: CollectionReference<T>,
  orgId: string,
  field: string,
  options: CountOptions = {},
  cadrage?: CadrageAcces,
): Promise<number | null> {
  const champ = aggregateField(field, 'runAverage');
  const { plan, q } = await aggregateQuery(col, orgId, options, cadrage);
  if (plan.vide) return null;
  try {
    const snap = await getAggregateFromServer(q, { valeur: averageOf(champ) });
    return snap.data().valeur;
  } catch (cause) {
    throw enrichIndexError(col.id, plan, cause, describeAggregateIndex(col.id, orgId, { ...options, where: { ...options.where, ...Object.fromEntries(plan.filters.filter(f => f.field === 'createdBy').map(f => [f.field, f.value])) } }, champ, cadrage));
  }
}

/**
 * Un `failed-precondition` Firestore veut dire "index composite manquant", et
 * son message renvoie vers un lien de console. Chez nous l'index vit dans le
 * DEPOT (`firebase/firestore.indexes.json`) : le lien ne sert a rien, le JSON
 * si. On renvoie donc la meme erreur enrichie du bloc a coller.
 */
export function enrichIndexError(
  collectionName: string,
  plan: ListPlan,
  cause: unknown,
  /* L'index d'une SOMME ou d'une MOYENNE, que le plan seul ne sait pas
     decrire : il ne connait pas le champ agrege, et sur des egalites seules il
     ne rendrait rien, donc le lien de console brut de Firestore. */
  agregat?: FirestoreIndex | null,
): unknown {
  const code = (cause as { code?: unknown } | null)?.code;
  if (code !== 'failed-precondition') return cause;
  const index = agregat ?? describeMissingIndex(collectionName, plan);
  if (index == null) return cause;
  return new MissingIndexError(collectionName, index, cause);
}
